import clsx from "clsx";
import * as React from "react";
import { type Ref, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

import styles from "./styles.module.css";
import { useSidebar } from "./use-sidebar";
import type { SidebarProps } from "./types";

import { SidebarContent } from "./SidebarContent";

const _Sidebar = (props: SidebarProps, ref: Ref<HTMLDivElement>) => {
  const {
    children,
    className,
    collapsible = "offcanvas",
    extraTitleButton,
    onEnter: onEnterProp,
    onEntered: onEnteredProp,
    onExit: onExitProp,
    onExited: onExitedProp,
    title,
    variant = "sidebar",
    ...rest
  } = props;
  const [isAnimating, setIsAnimating] = useState(false);
  const { isMobile, side, state } = useSidebar();
  const sidebarRef = useRef<HTMLDivElement>();

  const onEnter = () => {
    setIsAnimating(true);
    onEnterProp?.();
  };

  const onEntered = () => {
    setIsAnimating(false);
    onEnteredProp?.();
  };

  const onExit = () => {
    setIsAnimating(true);
    onExitProp?.();
  };

  const onExited = () => {
    setIsAnimating(false);
    onExitedProp?.();
  };

  const content = (
    <SidebarContent extraTitleButton={extraTitleButton} title={title}>
      {typeof children === "function"
        ? children({ isAnimating, state })
        : children}
    </SidebarContent>
  );

  if (collapsible === "none") {
    return (
      <div className={clsx(className)} ref={ref} {...props}>
        {content}
      </div>
    );
  }

  return (
    <CSSTransition
      in={state === "full-width"}
      nodeRef={sidebarRef}
      onEnter={onEnter}
      onEntered={onEntered}
      onExit={onExit}
      onExited={onExited}
      timeout={300}
    >
      <CSSTransition
        in={state === "expanded"}
        nodeRef={sidebarRef}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
        timeout={300}
      >
        <div
          className={clsx(styles.mainSidebar)}
          data-collapsible={state === "collapsed" ? collapsible : ""}
          data-is-mobile={isMobile ? "" : undefined}
          data-side={side}
          data-state={state}
          data-variant={variant}
          // @ts-expect-error TS is unable to infer the correct type for the render prop
          ref={sidebarRef}
        >
          <div className={styles.fakeSidebar} />
          <div className={clsx(styles.sidebar, className)} ref={ref} {...rest}>
            <div className={styles.sidebarContainer}>{content}</div>
          </div>
        </div>
      </CSSTransition>
    </CSSTransition>
  );
};

export const Sidebar = React.forwardRef(_Sidebar);
